/**
 * Page surface + status presentation — Sprint 5o.
 *
 * One source of truth for how a page's `surface` and `status` discriminators
 * render across the chrome (cockpit Pages panel, rail expansion, full-page
 * editor, new-page modal). Surface drives a coloured chip; status drives a
 * badge. Both are stored free-form server-side, so unknown values degrade to a
 * neutral grey rather than throwing.
 */

import type { Persona } from "../components/primitives";
import type { PageSurface, PageStatus } from "../../lib/types";

/**
 * Conventional surfaces, in the order the filter chips + picker present them.
 * Sprint 11 removed "draft" — it's a lifecycle state (see PAGE_STATUSES), not a
 * document type. The Draft/Draft collision in the panel chrome is gone.
 */
export const PAGE_SURFACES: PageSurface[] = [
  "spec",
  "strategy",
  "playbook",
  "notes",
  "reference",
  "meeting-note",
  // Sprint 21 — retrospectives (compose_retrospective output).
  "retrospective",
];

/** Conventional statuses, in workflow order. */
export const PAGE_STATUSES: PageStatus[] = [
  "draft",
  "ready",
  "in-progress",
  "blocked",
  "done",
  "archived",
];

interface SurfaceMeta {
  variant: Persona;
  label: string;
  /** CSS var for the small solid dot used in the rail. */
  dotVar: string;
}

const SURFACE_META: Record<PageSurface, SurfaceMeta> = {
  spec: { variant: "blue", label: "Spec", dotVar: "var(--kuvio-page-spec)" },
  strategy: { variant: "purple", label: "Strategy", dotVar: "var(--kuvio-page-strategy)" },
  playbook: { variant: "green", label: "Playbook", dotVar: "var(--kuvio-page-playbook)" },
  notes: { variant: "gray", label: "Notes", dotVar: "var(--kuvio-page-notes)" },
  reference: { variant: "teal", label: "Reference", dotVar: "var(--kuvio-page-reference)" },
  "meeting-note": { variant: "pink", label: "Meeting-note", dotVar: "var(--kuvio-page-meeting-note)" },
  retrospective: { variant: "amber", label: "Retrospective", dotVar: "var(--kuvio-page-notes)" },
};

/** Resolve a (possibly unknown / null) surface to its chip metadata. */
export function pageSurfaceMeta(surface: string | null | undefined): SurfaceMeta {
  if (surface && surface in SURFACE_META) return SURFACE_META[surface as PageSurface];
  return { variant: "gray", label: surface ? sentence(surface) : "Page", dotVar: "var(--c-gray-600)" };
}

interface StatusMeta {
  variant: Persona;
  label: string;
  /** CSS colour for the small status dot. */
  dotColor: string;
}

const STATUS_META: Record<PageStatus, StatusMeta> = {
  draft: { variant: "gray", label: "Draft", dotColor: "var(--c-gray-400)" },
  ready: { variant: "blue", label: "Ready", dotColor: "var(--c-blue-600)" },
  "in-progress": { variant: "amber", label: "In-progress", dotColor: "var(--c-amber-400)" },
  blocked: { variant: "red", label: "Blocked", dotColor: "var(--c-red-600)" },
  done: { variant: "green", label: "Done", dotColor: "var(--c-green-600)" },
  archived: { variant: "gray", label: "Archived", dotColor: "var(--c-gray-400)" },
};

/** Resolve a (possibly unknown / null) status to its badge metadata. */
export function pageStatusMeta(status: string | null | undefined): StatusMeta {
  if (status && status in STATUS_META) return STATUS_META[status as PageStatus];
  return { variant: "gray", label: status ? sentence(status) : "—", dotColor: "var(--c-gray-400)" };
}

/** Sentence-case a free-form token ("meeting-note" → "Meeting-note"). */
function sentence(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}
